import React from 'react';
import DynamicDataGrid from 'components/common/DataGridDevEx/DynamicDataGrid';
import { getProgramConfig } from './EnquiryGrid.constants';
import { createDataSource } from 'components/common/DataGridDevEx/DataGrid.hooks';
import { DataGrid } from 'devextreme-react';
import { Paper, withStyles } from '@material-ui/core';
import styles from './EnquiryGrid.styles';
import { getFilterByProgram } from './EnquiryGrid.filterRow';
import FilterRow from 'components/common/DataGridDevEx/FilterRow';
import moment from 'moment';
import { IFilterState, IEnquiryGridProps } from './EnquiryGrid.properties';
import { useRetrieveLines } from 'api/common/enquiryGrid/enquiryGrid';
import { PopupDialog, StyledPopupContentRenderer } from '../DataGridDevEx/DataGrid.components';
import { InfoCellComponent } from './InfoCellComponent';

const EnquiryGrid = ({ classes, program, defaultView, selectedCustomer, selectedProduct, path, onInitialLoad, customPopupContentRenderer }: IEnquiryGridProps) => {
    const dataGridInnerRef = React.useRef<DataGrid>();
    const filterButtonRef = React.useRef<HTMLButtonElement>();
    const filterRef = React.useRef({ isOpen: false });
    const [filters, setFilters] = React.useState({ ...getProgramConfig(program?.name, 'filterState'), EntityView: defaultView ?? '' });
    const [popupOpen, setPopupOpen] = React.useState(false);
    const [popupSelectedData, setPopupSelectedData] = React.useState(null);
    const fetchLinesMutation = useRetrieveLines(program);
    const detailOnClick = React.useCallback(
        ({ data }) => {
            // Todo: make it generic by passing identifiers in program props
            if (program?.module === 'inventory') {
                onInitialLoad(program?.detailsPath, {
                    ProductId: Number(data.ProductId).toString() ?? '',
                    Warehouse: data.Warehouse ?? ''
                });
            } else {
                onInitialLoad(program?.detailsPath, {
                    CustomerId: Number(data.Customer).toString() ?? '',
                    TransactionId: data.TransactionId ?? ''
                });
            }
        },
        [onInitialLoad]
    );

    const handleInfoClick = React.useCallback(
        (rowData) => {
            setPopupSelectedData(rowData);
            setPopupOpen(true);
        },
        []
    );

    React.useEffect(
        () => {
            if (path) onInitialLoad(program?.name);
        },
        [path, onInitialLoad]
    );

    const fetchLines = React.useCallback(
        async (BatchPage, BatchSize, Sort) => {
            // ToDo: Improve this check to be more generic
            if (Number(selectedCustomer?.CustomerId) || Number(selectedProduct?.ProductId)) {
                try {
                    // TODO: Make filters separate for each program and pass it from outside as config
                    const config = getFilterByProgram(program?.name);

                    const payload: any = {
                        ...(selectedCustomer?.CustomerId && { CustomerId: selectedCustomer.CustomerId }),
                        ...(selectedProduct?.ProductId && { ProductId: Number(selectedProduct.ProductId) }),
                        urlQueryParams: {
                            BatchPage,
                            BatchSize,
                            Sort
                        }
                    };

                    // Only attempt to add filters if that program actually has filters
                    if (config?.fields && filters) {
                        config.fields.forEach((field) => {
                            let value = filters[field];

                            // Handle date formatting generically
                            if (field === 'FromDate' && value) {
                                value = moment(value, 'DD/MM/YYYY').format('YYYY-MM-DD');
                            }
                            if (field === 'ToDate' && value) {
                                value = moment(value, 'DD/MM/YYYY').format('YYYY-MM-DD');
                            }

                            // Exclude invalid or empty values
                            if (
                                value !== undefined &&
                                value !== null &&
                                value !== '' &&
                                value !== '0'
                            ) {
                                payload[field] = value;
                            }
                        });
                    }
                    const lines = await fetchLinesMutation.mutateAsync(payload);

                    return lines?.[program?.APIResponseKey];

                } catch (error) {
                    console.error('Error fetching lines:', error);

                    return null;
                }
            } else {
                return null;
            }
        },
        [selectedCustomer?.CustomerId, selectedProduct?.ProductId, filters]
    );

    const dataSource = React.useMemo(
        () => createDataSource(
            program?.key,
            { fetch: fetchLines },
        ),
        [fetchLines]
    );
    const filterOnApply = ({ filters: appliedFilters }: { filters: IFilterState }) => {
        setFilters((prev) => ({
            ...prev,
            ...appliedFilters
        }));
    };

    const columns = React.useMemo(
        () => {
            return getProgramConfig(program?.name, 'columns')?.map((col) => {
                if (col.cellComponent === 'InfoCell') {
                    return {
                        ...col,
                        cellComponent: (props) =>
                            <InfoCellComponent {...props} onInfoClick={handleInfoClick} />
                    };
                }

                return col;
            });
        },
        [program?.name, handleInfoClick]
    );

    return (
        <div className={program?.module === 'inventory' ? classes.summaryPanelContainer : classes.gridContainer}>
            <Paper className={program?.module === 'inventory' ? classes.summaryPanelGrid : classes.parentContainer}>
                {
                    getFilterByProgram(program?.name) && (
                        <FilterRow
                            filterButtonRef={filterButtonRef}
                            disableInitialApplyCall={true}
                            parameters={getFilterByProgram(program?.name)?.component.parameters}
                            formName={getFilterByProgram(program?.name)?.component.formName}
                            onApplyFilters={filterOnApply}
                            initialValues={{ ...filters }}
                            validate={getFilterByProgram(program?.name)?.component.validate}
                            rowModelType={getFilterByProgram(program?.name)?.component.rowModelType}
                            filterRef={filterRef}
                        />
                    )
                }
                <DynamicDataGrid
                    dataGridClassName={classes.grid}
                    columns={columns}
                    allowHeaderFilting={false}
                    allowUpdating={false}
                    allowDelete={false}
                    actionButtonsVisibility={false}
                    selection='none'
                    gridProps={{
                        dataSource: dataSource,
                        dataGridRef: dataGridInnerRef,
                        sorting: { mode: 'single' },
                        selection: { mode: 'none' }
                    }}
                    detailOnClick={program?.detailsPath ? detailOnClick : undefined}
                />
            </Paper>
            <PopupDialog
                open={popupOpen}
                title='Details'
                onOk={() => setPopupOpen(false)}
            >
                <StyledPopupContentRenderer
                    selectedData={popupSelectedData}
                    customRenderer={customPopupContentRenderer}
                    popupText='No details available.'
                />
            </PopupDialog>
        </div>
    );
};
export default withStyles(styles, { index: 1 })(React.memo(React.forwardRef(EnquiryGrid)));
